import { useState, useEffect } from "react";
import { clsx } from "clsx";
import { PromoExpandedContent } from "@/components/units/unit/info/PromoExpandedContent";
import { InlineReservationForm } from "@/components/units/unit/info/InlineReservationForm";
import type { UnitGroup } from "@/types";
import type { PromoExpandedBy } from "@/context/UnitItemContext";
import { useSmoothScrollIntoView } from "@/hooks/useSmoothScrollIntoView";

interface GridPromoExpansionProps {
    unitGroup: UnitGroup;
    expandedBy: PromoExpandedBy;
    /** Explicit grid row for placement (1-based). */
    gridRow: number;
    /** Whether the expansion is visually open (controlled by parent for animation) */
    isOpen: boolean;
    /** Skip the multi-promo selector step and open straight to the form (Layout
     *  1, where the promo is chosen on the card's "Choose Your Special" dropdown). */
    skipSelector?: boolean;
    /** The card-selected promo to use when skipSelector is set. */
    selectedPromo?: number;
    /** Featured color of the expanded unit (only set when it is featured); drives the panel's
     *  outline/tint so it matches that unit. Non-featured units keep the primary outline. */
    featuredColor?: string;
    /** Featured "-light" tint of the expanded unit (host-computed); falls back to a color-mix. */
    featuredColorLight?: string;
}

/**
 * Grid promo expansion — a full-width sibling of the unit cards in the parent
 * `.unit-table` grid, spanning all columns and pushing the cards after it down.
 * The parent controls `isOpen` to drive the expand/collapse transition and
 * delays unmount until it finishes. Used by both widget layouts.
 */
export const GridPromoExpansion = ({
    unitGroup,
    expandedBy,
    gridRow,
    isOpen,
    skipSelector = false,
    selectedPromo,
    featuredColor,
    featuredColorLight,
}: GridPromoExpansionProps) => {
    const { rates } = unitGroup;
    const containerRef = useSmoothScrollIntoView<HTMLDivElement>(isOpen);

    // Internal selection drives the in-expansion selector (Layout 2). When
    // skipSelector is set (Layout 1), the card's dropdown promo is used instead.
    const [internalPromo, setInternalPromo] = useState<number>(() => {
        return rates.promo_rates[0]?.id ?? 0;
    });
    const formPromo = skipSelector
        ? selectedPromo ?? rates.promo_rates[0]?.id ?? 0
        : internalPromo;

    // Quote and reserve both use the in-row stepped form flow.
    const isFormExpanded = expandedBy === "quote" || expandedBy === "reserve";
    // Multi-promo is a two-step in-row flow: promo selector → action form.
    const [showActionForm, setShowActionForm] = useState(false);
    useEffect(() => {
        if (!isFormExpanded) setShowActionForm(false);
    }, [isFormExpanded]);

    if (!expandedBy) return null;

    const hasMultiplePromos = rates.promo_rates.length > 1;
    // skipSelector (Layout 1): straight to the form. Otherwise single/no-promo
    // goes straight to the form; multi-promo shows the selector first, advancing
    // to the form when the user picks a promo + Continue.
    const showFormStep =
        isFormExpanded &&
        (skipSelector || !hasMultiplePromos || showActionForm);

    // Only featured units pass a color; non-featured panels keep the base primary outline.
    const isFeatured = Boolean(featuredColor);
    const featuredStyle = isFeatured
        ? ({
              "--featured-color": featuredColor,
              ...(featuredColorLight
                  ? { "--featured-color-light": featuredColorLight }
                  : {}),
          } as React.CSSProperties)
        : undefined;

    return (
        <div
            ref={containerRef}
            className={clsx(
                "unit-item__promotions-expanded--grid",
                isOpen && "unit-item__promotions-expanded--grid--open",
                isFeatured && "unit-item__promotions-expanded--grid--featured",
            )}
            id={`promo-panel-${unitGroup.representative_unit.id}`}
            role="region"
            aria-label={
                showFormStep
                    ? expandedBy === "reserve"
                        ? "Reserve form"
                        : "Get a Quote form"
                    : "Promotional discount selection"
            }
            style={{ gridRow, gridColumn: "1 / -1", ...featuredStyle }}
        >
            <div
                className={clsx(
                    "promotion-selection",
                    showFormStep && "quote-selection",
                )}
            >
                {showFormStep ? (
                    <InlineReservationForm
                        mode={expandedBy === "reserve" ? "reserve" : "lead"}
                        unitGroup={unitGroup}
                        selectedPromo={formPromo}
                        onBack={
                            !skipSelector && hasMultiplePromos
                                ? () => setShowActionForm(false)
                                : undefined
                        }
                    />
                ) : (
                    <PromoExpandedContent
                        promotions={rates.promo_rates}
                        selectedPromo={internalPromo}
                        onPromoChange={setInternalPromo}
                        unitGroup={unitGroup}
                        expandedBy={expandedBy}
                        onRequestQuoteForm={() => setShowActionForm(true)}
                        onRequestReserveForm={() => setShowActionForm(true)}
                    />
                )}
            </div>
        </div>
    );
};
